import { useEffect, useState } from "react";
import history from "utils/history";
import ParticipantScreen from "screens/SignRequest/ParticipantScreen/ParticipantScreen";
import { FIELD_COMMIT_EVENT } from "screens/SignRequest/ParticipantScreen/ParticipantScreenService";
import { globalEmitter } from "utils/emitter";
import { EMBED_SIGN_PATH } from "./embedMode";
import {
  EmbedErrorCode,
  detectFrameOrigin,
  embedErrorCode,
  postEmbedEventOnce,
  setEmbedContext,
  startEmbedChannel,
} from "./embedBridge";
import { exchangeEmbedToken, reportEmbedSignStarted } from "./embedService";
import { EmbedError, EmbedLoading } from "./EmbedPanel";

/** The one-time token arrives in the URL fragment, which browsers never send to a server or CDN log. */
const readFragmentToken = (): string => {
  const match = /(?:^#|&)token=([^&]+)/.exec(window.location.hash);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escape (e.g. "%E0%A4%A") throws; treat it as no token so the panel shows TOKEN_INVALID.
    return "";
  }
};

/**
 * The parent origin after a reload, when there is no token left to exchange: the origin the browser
 * itself reports for the embedding page, never one read from the URL. Events are posted only to it.
 */
const reloadParentOrigin = (): string | null => detectFrameOrigin() || null;

/**
 * /embed/sign -- the signer widget. Redeems the one-time token, then runs the normal signing screen in
 * embed mode with the short-lived requestToken/key the exchange returned. A reload inside the key's
 * lifetime resumes the same session (the key is already on the URL); after that the partner mints again.
 */
const EmbedSignScreen = () => {
  const [state, setState] = useState<"loading" | "ready">("loading");
  const [errorCode, setErrorCode] = useState<EmbedErrorCode | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = readFragmentToken();
    if (window.location.hash) {
      // Drop the token from the address bar and history at once.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    const start = async () => {
      if (token) {
        try {
          const exchanged = await exchangeEmbedToken(token);
          if (exchanged.scope !== "signing" || !exchanged.requestToken || !exchanged.key) {
            setErrorCode("TOKEN_INVALID");
            return;
          }
          setEmbedContext({ scope: "signing", requestId: exchanged.requestId });
          startEmbedChannel(exchanged.parentOrigin);
          const search = new URLSearchParams();
          search.set("id", exchanged.requestToken);
          search.set("key", exchanged.key);
          search.set("signer", "true");
          history.replace(`${EMBED_SIGN_PATH}?${search.toString()}`);
          setState("ready");
        } catch (error: any) {
          // No trusted parent origin yet, so nothing is posted -- the panel is all the partner sees.
          setErrorCode(embedErrorCode(error));
        }
        return;
      }
      if (params.get("id") && params.get("key")) {
        const parentOrigin = reloadParentOrigin();
        setEmbedContext({ scope: "signing" });
        if (parentOrigin) startEmbedChannel(parentOrigin);
        setState("ready");
        return;
      }
      setErrorCode("TOKEN_INVALID");
    };
    start();
  }, []);

  // sign_started: the first field commit (a value set or a signature adopted) of this page load.
  useEffect(() => {
    if (state !== "ready") return;
    // Read once, when the widget becomes ready: the URL already carries this session's id and key by then,
    // so a later in-app navigation cannot change what is reported.
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    const key = params.get("key");
    const onFirstCommit = () => {
      globalEmitter.removeListener(FIELD_COMMIT_EVENT, onFirstCommit);
      postEmbedEventOnce("sign_started", { signerRole: "Signer" });
      if (id && key) {
        reportEmbedSignStarted(id, key).catch(() => undefined);
      }
    };
    globalEmitter.on(FIELD_COMMIT_EVENT, onFirstCommit);
    return () => {
      globalEmitter.removeListener(FIELD_COMMIT_EVENT, onFirstCommit);
    };
  }, [state]);

  if (errorCode) return <EmbedError code={errorCode} />;
  if (state === "loading") return <EmbedLoading />;
  return <ParticipantScreen />;
};

export default EmbedSignScreen;
